'use client';

import { useTranslation } from '@/hooks/useTranslation';
import BookDemoSection from '@/components/BookDemoSection';
import { FaqAccordion, PageSection, SectionTitle } from '@/components/ui';
import { MetricsBand } from '@/components/primitives/MetricsBand';
import TechnologyHero from './TechnologyHero';
import IndustryBattleground from '@/components/industry/IndustryBattleground';
import IndustryChallenges from '@/components/industry/IndustryChallenges';
import PlatformCapabilities from '@/components/industry/PlatformCapabilities';
import IndustryResults from '@/components/industry/IndustryResults';
import { getIndustryContent } from '@/lib/industryContent';
import '@/components/industry/industry-page.css';

/* Problem-framing stats for the MetricsBand — the approved data points that
   previously lived in the hero (sourced: G2, Stack Overflow, Gartner 2025). */
const METRICS = [
  { value: '94%', label: 'of enterprise software buyers read G2 or Capterra reviews before shortlisting a vendor (G2 State of Software 2025)' },
  { value: '65%', label: 'of developers say community reputation directly influences which tools they adopt or recommend (Stack Overflow Developer Survey 2025)' },
  { value: '3x', label: 'faster brand trust erosion for tech companies following unmanaged product update backlash vs proactive response (Gartner 2025)' },
];

export default function TechnologyBody() {
  const { t } = useTranslation();
  const C = getIndustryContent('technology');

  const CHALLENGES = [
    {
      title: t('industries_technology.the_developer_community_blind_spot'),
      desc: t('industries_technology.developers_discuss_critique_and_adv'),
    },
    {
      title: t('industries_technology.the_product_launch_window'),
      desc: t('industries_technology.a_pricing_model_change_deprecated_a'),
    },
    {
      title: t('industries_technology.the_review_platform_gap'),
      desc: t('industries_technology.g2_capterra_review_management_is_a'),
    },
    {
      title: t('industries_technology.the_silent_churn_signal'),
      desc: t('industries_technology.enterprise_customers_dont_always_ca'),
    },
  ];

  const FAQS = [
    {
      q: t('industries_technology.what_is_a_technology_brand_reputati'),
      a: t('industries_technology.one_workspace_for_developer_communi'),
    },
    {
      q: t('industries_technology.how_does_locobuzz_monitor_developer'),
      a: t('industries_technology.real_time_monitoring_across_reddit'),
    },
    {
      q: t('industries_technology.can_locobuzz_track_product_launch_s'),
      a: t('industries_technology.yes_live_sentiment_dashboards_from'),
    },
    {
      q: t('industries_technology.what_are_contextualpulse_and_signal'),
      a: t('industries_technology.contextualpulse_identifies_whether'),
    },
    {
      q: t('industries_technology.how_does_locobuzz_handle_g2_and_cap'),
      a: t('industries_technology.g2_capterra_trustpilot_product_hunt'),
    },
    {
      q: t('industries_technology.can_locobuzz_detect_early_saas_chur'),
      a: t('industries_technology.yes_signalsense_monitors_social_and'),
    },
    {
      q: t('industries_technology.how_does_locobuzz_help_technology_c'),
      a: t('industries_technology.real_time_keyword_alert_systems_det'),
    },
  ];

  const BG_TITLE = t('industries_technology.why_is_brand_intelligence_now_missi');
  const BG_ACCENT = 'Technology Companies?';
  const _bgCut = BG_TITLE.lastIndexOf(BG_ACCENT);
  const bgTitle = _bgCut > 0 ? BG_TITLE.slice(0, _bgCut).trim() : BG_TITLE;
  const bgAccent = _bgCut > 0 ? BG_TITLE.slice(_bgCut) : undefined;

  return (
    <div className="industry-page-wrap">
      <span className="sr-only">{t('industries_technology.locobuzz_is_an_ainative_brand_intel')}</span>
      <span className="sr-only">{t('industries_technology.in_technology_brand_reputation_is_b')}</span>
      <span className="sr-only">{t('industries_technology.locobuzz_maps_six_purposebuilt_capa')}</span>
      <span className="sr-only">{t('industries_technology.technology_brands_using_locobuzz_re')}</span>

      <TechnologyHero />

      <MetricsBand items={METRICS} columns={3} />

      <IndustryBattleground
        eyebrow={t('industries_technology.why_brand_intelligence_is_now_missi')}
        title={bgTitle}
        titleAccent={bgAccent}
        paragraph={t('industries_technology.in_a_space_where_audiences_are_deve')}
        imageSrc="/industries/why/technology.webp"
        imageAlt={t('industries_technology.technology_brand_sentiment_and_supp')}
        bg="tint"
      />

      <IndustryChallenges
        title={t('industries_technology.what_brand_cx_challenges_do_saas_co')}
        items={CHALLENGES}
        layout="row5"
        align="center"
        bg="page"
      />

      <PlatformCapabilities {...C.capabilities} />

      <IndustryResults {...C.results} />

      <PageSection id="faqs" bg="tint">
        <SectionTitle
          title={
            <>
              {t('faq.everything_you_need_to')}{' '}
              <span className="serif-accent italic">{t('faq.know')}</span>
            </>
          }
          align="center"
        />
        <FaqAccordion items={FAQS} columns={2} name="technology-faq" />
      </PageSection>

      <BookDemoSection />
    </div>
  );
}
